import React, { useState, useEffect } from 'react';
import axiosInstance from '../axios.config';
import { ToastContainer, toast } from 'react-toastify';
import { Box, Typography } from '@mui/material';
import { ContentBlock } from '../shared/sharedStyles';
import { UserGroupTable } from '../components/UserGroup/UserGroupTable';
import { UpdateUserGroupModal } from '../components/UserGroup/UpdateUserGroupModal';
import { CreateUserGroupModal } from '../components/UserGroup/CreateUserGroupModal';
import { FilteringZone } from '../components/UserGroup/FilteringZone';

const UserGroups = () => {
    const lngsltd = JSON.parse(localStorage.getItem('languageSelected'));
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const [showAdd, setShowAdd] = useState(false);
    const handleCloseAdd = () => setShowAdd(false);
    const handleShowAdd = () => setShowAdd(true);

    // For filters option
    const [search, setSearch] = useState('');
    const [filteredData, setFilteredData] = useState([]);

    // For Adding new UserGroup
    const [userIDs, setUserIDs] = useState([]);
    const [groupIDs, setGroupIDs] = useState([]);

    // For Updating existing UserGroup
    const [editID, setEditID] = useState('');
    const [editUserIDs, setEditUserIDs] = useState([]);
    const [editGroupIDs, setEditGroupIDs] = useState([]);


    const [userData, setUserData] = useState([]);
    const [groupData, setGroupData] = useState([]);
    const [data, setData] = useState([]);

    const [loadingUserData, setLoadingUserData] = useState(true);
    const [loadingGroupData, setLoadingGroupData] = useState(true);
    const [loadingData, setLoadingData] = useState(true);

    useEffect(() => {
        getData();
        fetchUserData();
        fetchGroupData();
    }, []);

    useEffect(() => {
        // Ensure all necessary data is available before filtering
        if (!data.length || !userData.length || !groupData.length) {
            return;  // Exit early if any of the required data is not loaded
        }

        // If search is empty, just show all data
        if (!search.trim()) {
            setFilteredData(data);
            return;
        }

        const searchLower = search.toLowerCase().trim(); // Normalize search term        
        const filteredUserGroups = data.filter(userGroup => {
            // Get user and group based on IDs
            const user = userData.find(u => u.userID === userGroup.userID);
            const group = groupData.find(g => g.groupID === userGroup.groupID);

            // Check if user or group is missing before trying to access properties
            const userName = user?.userName?.toLowerCase() || '';  // Default to empty string if undefined        
            const groupName = group?.groupName?.toLowerCase() || '';  // Default to empty string if undefined       

            // Check if either user or group name matches the search term
            return userName.includes(searchLower) || groupName.includes(searchLower);
        });

        // Update filtered data with results
        setFilteredData(filteredUserGroups);
    }, [search, data, userData, groupData]);






    const fetchUserData = () => {
        axiosInstance.get(`/User`)
            .then((result) => {
                setUserData(result.data);
                setLoadingUserData(false); // Set loading to false after data is fetched
            })
            .catch(() => setLoadingUserData(false)); // Handle error and set loading to false
    };

    const fetchGroupData = () => {
        axiosInstance.get(`/Group`)
            .then((result) => {
                setGroupData(result.data);
                setLoadingGroupData(false); // Set loading to false after data is fetched
            })
            .catch(() => setLoadingGroupData(false)); // Handle error and set loading to false
    };

    const getData = () => {
        axiosInstance.get(`/UserGroup`)
            .then((result) => {
                setData(result.data);
                setLoadingData(false); // Set loading to false after data is fetched
            })
            .catch(() => setLoadingData(false)); // Handle error and set loading to false
    };

    const getUserById = (userId) => {
        const user = userData.find((user) => user.userID === userId);
        return user ? user.username : lngsltd['No User Found'];
    };

    const getGroupNames = (groupIDs) => {
        if (!groupIDs) return lngsltd['No Group Found'];

        // normalize single number to array
        const idsArray = Array.isArray(groupIDs) ? groupIDs : [groupIDs];

        const names = idsArray
            .map(id => {
                const group = groupData.find(g => g.groupID === id);
                return group ? group.groupName : null;
            })
            .filter(name => name !== null);

        return names.length > 0 ? names.join(', ') : lngsltd['No Group Found'];
    };


    const handleEdit = (row) => {

        if (!row || !row.userID) {
            return;
        }

        handleShow();

        setEditUserIDs([row.userID]);
        setEditGroupIDs(row.groupIDs || []);
    };


    const handleDelete = (userID) => {
        if (window.confirm(lngsltd['Are you sure to delete this UserGroup?'])) {
            axiosInstance.delete(`/UserGroup/${userID}`)
                .then(() => {
                    toast.success(lngsltd['UserGroup has been deleted']);
                    getData();
                })
                .catch((error) => {
                    toast.error(error);
                });
        }
        handleClearFilters();
    };

    const handleUpdate = () => {
        const updatedData = {
            userIDs: editUserIDs,
            groupIDs: editGroupIDs
        };

        axiosInstance.post("/UserGroup", updatedData)
            .then(() => {
                handleClose();
                getData();
                clear();
                toast.success(lngsltd['UserGroup has been updated']);
            })
            .catch(() => {
                toast.error("Error while updating groups");
            });
    };

    const handleSave = () => {
        const url = `/UserGroup`;
        const newData = {
            userIDs,
            groupIDs,   // send array
        };
        axiosInstance.post(url, newData)
            .then(() => {
                handleCloseAdd();
                getData();
                clear();
                toast.success(lngsltd['UserGroup has been added']);
            })

            .catch((error) => {
                // Check if the error response has the expected structure
                if (error.response && error.response.data && error.response.data.errorMessage) {
                    toast.error(error.response.data.errorMessage);
                } else {
                    toast.error("Error while assigning groups");
                }
            });
    };

    const handleClearFilters = () => {
        setSearch('');
    };

    const clear = () => {
        setUserIDs('');
        setGroupIDs([]);
        setEditGroupIDs([]);
        setEditUserIDs('');
        setEditID('');
    };

    return (
        <Box className='fullHW' px={6} py={4} display="flex" flexDirection="column">
            <Typography variant="h5" fontWeight={600}>{lngsltd["List of UserGroups"]}</Typography>
            <ToastContainer
                autoClose={2000}
                progressClassName='custom-toast-progress-bar' />
            <ContentBlock sx={{ flexGrow: 1, display: "flex", flexDirection: "column", py: 4, "& p": { m: 0 } }}>
                <FilteringZone
                    handleShowAdd={handleShowAdd}
                    lngsltd={lngsltd}
                    search={search}
                    setSearch={setSearch}
                    handleClearFilters={handleClearFilters} />
                <Box mt={2} flexGrow={1} overflow="hidden" width="90%">
                    <UserGroupTable
                        lngsltd={lngsltd}
                        data={filteredData}
                        filteredData={filteredData}
                        groupData={groupData}
                        userData={userData}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                    />
                </Box>
                <UpdateUserGroupModal
                    show={show}
                    handleClose={handleClose}
                    lngsltd={lngsltd}
                    editUserIDs={editUserIDs}
                    setEditUserIDs={setEditUserIDs}
                    userData={userData}
                    editGroupIDs={editGroupIDs}
                    setEditGroupIDs={setEditGroupIDs}
                    groupData={groupData}
                    handleUpdate={handleUpdate}
                />

                <CreateUserGroupModal
                    showAdd={showAdd}
                    handleCloseAdd={handleCloseAdd}
                    lngsltd={lngsltd}
                    userIDs={userIDs}
                    setUserIDs={setUserIDs}
                    userData={userData}
                    groupIDs={groupIDs}
                    setGroupIDs={setGroupIDs}
                    groupData={groupData}
                    handleSave={handleSave}
                />
            </ContentBlock>
        </Box>
    );
};

export default UserGroups;






